import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LogOut, HeartPulse } from "lucide-react";

const NAVY = "#032866";
const YELLOW = "#fdda0d";

/**
 * FieldLayout — the caregiver (PCW) shell.
 *
 * Staff/PCW isolation, Phase 3: caregivers render inside this clean shell
 * instead of the office MainLayout, so they NEVER load office widgets
 * (sidebar, team presence, notifications, HealthcareNav) or see office menus.
 * Only genuine field-self routes mount here.
 */
export function FieldLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) return <Navigate to="/pcw-login" replace />;
  // Office accounts don't belong in the caregiver shell.
  if (user && user.role !== "pcw") return <Navigate to="/" replace />;

  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Caregiver";
  const initials =
    (user?.first_name?.[0] || "") + (user?.last_name?.[0] || "") ||
    (user?.email?.[0] || "C").toUpperCase();

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/pcw-login", { replace: true });
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F4F7FB" }}>
      <header
        style={{ background: NAVY }}
        className="sticky top-0 z-30 shadow-md"
        data-testid="field-shell-header"
      >
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: YELLOW }}
            >
              <HeartPulse className="w-4 h-4" style={{ color: NAVY }} />
            </div>
            <div className="min-w-0">
              <div className="text-white font-black text-[13px] leading-tight truncate">
                Avida Care
              </div>
              <div
                className="text-[10px] font-bold tracking-[0.15em] uppercase leading-tight"
                style={{ color: YELLOW }}
              >
                Caregiver Portal
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black"
                style={{ background: "rgba(255,255,255,0.14)", color: "white" }}
              >
                {initials}
              </div>
              <span
                className="text-white/90 text-[12.5px] font-semibold truncate max-w-[140px]"
                data-testid="field-user-name"
              >
                {name}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12.5px] font-semibold transition-colors"
              data-testid="field-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main data-testid="field-shell-content">
        <Outlet />
      </main>
    </div>
  );
}

export default FieldLayout;
